import { registerMockAPI } from '~/mocks/mockApis';

import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';
import { transformToSelectOptions } from '~/utils/common';

import { API_ENDPOINT } from '~/constants';

import { deliveryAgencyKeys } from './keys';

import type { GetCollectingRequestsSearchConditionsResponse } from '~/types/delivery-agency.types';

export const fetchCollectingRequestsSearchConditions = async () => {
  const { data } = await axiosInstance.get<GetCollectingRequestsSearchConditionsResponse>(
    API_ENDPOINT.DELIVERY_AGENCY.GET_COLLECTING_REQUESTS_CODES
  );
  return data.data;
};

type UseGetCollectingRequestsSearchConditionsQueryOptions = {
  enabled?: boolean;
  showGlobalErrorToast?: boolean;
};

export const useGetCollectingRequestsSearchConditionsQuery = (
  options?: UseGetCollectingRequestsSearchConditionsQueryOptions
) => {
  return useQuery({
    queryKey: deliveryAgencyKeys.collectingRequestsSearchConditions(),
    queryFn: fetchCollectingRequestsSearchConditions,
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 30,
    meta: { showGlobalErrorToast: options?.showGlobalErrorToast ?? true },
    select: (data) => ({
      searchKeywordTypes: transformToSelectOptions(data.searchKeywordTypes, 'code', 'description'),
      collectingRequestStatuses: transformToSelectOptions(data.collectingRequestStatuses, 'code', 'description'),
      collectedProductHandlings: transformToSelectOptions(data.collectedProductHandlings, 'code', 'description'),
    }),
  });
};

registerMockAPI<GetCollectingRequestsSearchConditionsResponse>({
  method: 'get',
  endpoint: API_ENDPOINT.DELIVERY_AGENCY.GET_COLLECTING_REQUESTS_CODES,
  label: '배송 주문 반품 신청·조회/검색조건',
  scenarios: [
    {
      name: '정상 응답',
      response: {
        data: {
          searchKeywordTypes: [
            { code: 'COLLECTING_REQUEST_CODE', description: '반품요청번호' },
            { code: 'COLLECTING_SHIPPING_LABEL_NUMBER', description: '회수 운송장번호' },
            { code: 'OUTBOUND_CODE', description: '출고번호' },
            { code: 'CLIENT_ORDER_CODE', description: '판매처 주문번호(원주문)' },
            { code: 'RECEIVER_NAME', description: '원주문 수령자명' },
            { code: 'ORDERER_NAME', description: '원주문 주문자명' },
          ],
          collectingRequestStatuses: [
            { code: 'COLLECT_ACCEPTED', description: '회수 예정' },
            { code: 'COLLECT_DEPARTURE_READY', description: '회수 출발 준비' },
            { code: 'COLLECT_ON_THE_WAY', description: '회수 중' },
            { code: 'COLLECT_COMPLETED', description: '회수 완료' },
            { code: 'COLLECT_FAILED', description: '회수 실패' },
            { code: 'COLLECT_CANCELED', description: '회수 취소' },
          ],
          collectedProductHandlings: [
            { code: 'DISPOSE', description: '배송사 폐기' },
            { code: 'RETURN_TO_SENDER', description: '다시 돌려받기(반송)' },
          ],
        },
      },
    },
    {
      name: '서버 에러 (500)',
      status: 500,
      response: { data: { searchKeywordTypes: [], collectingRequestStatuses: [], collectedProductHandlings: [] } },
    },
  ],
});
